import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  CheckCircle2,
  Flag,
  LayoutGrid,
  LogIn,
  MountainSnow,
  Route,
} from "lucide-react";
import { fetchCollections, type Collection } from "../api/collections";
import { companionNote, isCompanionCollection } from "@atlas/shared";
import { useAuth } from "../hooks/useAuth";
import { usePageMeta } from "../hooks/usePageMeta";
import { AtlasPanel } from "../components/AtlasPanel";
import { ProgressRing } from "../components/ProgressRing";
import { ProgressBar } from "../components/ProgressBar";
import { CollectionCard, trailDistanceOf } from "../components/CollectionCard";
import { Badge } from "../components/Badge";
import { Skel } from "../components/Skeleton";
import { EmptyState } from "../components/EmptyState";
import { SiteSearch } from "../components/SiteSearch";
import { CollectionsDirectory, type DirectoryFilter } from "./CollectionsDirectory";

const TYPE_ICON: Record<string, React.ReactNode> = {
  "Peak List": <MountainSnow size={19} />,
  peaks: <MountainSnow size={19} />,
  Peaks: <MountainSnow size={19} />,
  "National Trail": <Route size={19} />,
  Trails: <Route size={19} />,
  trails: <Route size={19} />,
  Landmarks: <Flag size={19} />,
  landmarks: <Flag size={19} />,
  Challenge: <CheckCircle2 size={19} />,
};

const TYPE_ICON_LG: Record<string, React.ReactNode> = {
  "Peak List": <MountainSnow size={26} />,
  peaks: <MountainSnow size={26} />,
  Peaks: <MountainSnow size={26} />,
  "National Trail": <Route size={26} />,
  Trails: <Route size={26} />,
  trails: <Route size={26} />,
  Landmarks: <Flag size={26} />,
  landmarks: <Flag size={26} />,
  Challenge: <CheckCircle2 size={26} />,
};

/**
 * A collection that is exactly one trail has no page of its own worth
 * visiting: the trail page shows the same progress section by section, so
 * every card goes straight there (ATL-132).
 */
function collectionTarget(c: Collection) {
  return c.trailSlug
    ? ({ to: "/trails/$id", params: { id: c.trailSlug } } as const)
    : ({ to: "/collections/$slug", params: { slug: c.slug } } as const);
}

function pctOf(c: Collection) {
  // The server pct includes fractional credit for partially-walked trails
  // and partially-covered regions (ATL-48) — a 3/9-sections trail collection
  // reads 33%, not 0%. Count-based maths is only a fallback for stale caches.
  if (typeof c.pct === "number" && Number.isFinite(c.pct)) return Math.round(c.pct);
  return c.itemCount > 0 ? Math.round((c.completedCount / c.itemCount) * 100) : 0;
}

/**
 * /collections and its filtered aliases /hills, /trails, /landmarks. Signed
 * out this is the public directory (CollectionsDirectory, ATL-132); signed in
 * it is the completionist view below with the same search box added.
 */
export default function CollectionsPage({ typeFilter = "all" }: { typeFilter?: DirectoryFilter } = {}) {
  const { user: me, isLoading: authLoading } = useAuth();
  const isAuthenticated = !!me;

  const { data, isLoading } = useQuery({
    queryKey: ["collections"],
    queryFn: fetchCollections,
  });
  // ATL-168: companion lists (Munro Tops) appear inside their parent's page,
  // never as tiles of their own, signed in or out.
  const collections = (data?.collections ?? []).filter((c) => !isCompanionCollection(c));

  if (!isAuthenticated) {
    return (
      <CollectionsDirectory
        filter={typeFilter}
        collections={collections}
        allCollections={data?.collections ?? []}
        isLoading={isLoading || authLoading}
      />
    );
  }
  return (
    <AuthedCollectionsPage
      typeFilter={typeFilter}
      collections={collections}
      allCollections={data?.collections ?? []}
      isLoading={isLoading}
    />
  );
}

function AuthedCollectionsPage({
  typeFilter,
  collections,
  allCollections,
  isLoading,
}: {
  typeFilter: DirectoryFilter;
  collections: Collection[];
  /** Unfiltered list incl. companion lists, for the "+ 52 tops" notes (ATL-168). */
  allCollections: Collection[];
  isLoading: boolean;
}) {
  const navigate = useNavigate();
  const isAuthenticated = true;
  const [filterType, setFilterType] = React.useState<string>(typeFilter);

  usePageMeta({
    title: "Collections | Atlas",
    description:
      collections.length > 0
        ? `${collections.length} curated collections to complete — Munros, Wainwrights, National Trails and more, with live completion tracking.`
        : "Browse Atlas's curated collections — Munros, Wainwrights, National Trails and more, with live completion tracking.",
    canonicalPath: "/collections",
  });
  const completed = collections.filter((c) => c.completedCount >= c.itemCount);
  const inProgress = collections.filter((c) => c.completedCount < c.itemCount);
  const avg =
    collections.length > 0
      ? Math.round(
          collections.reduce((s, c) => s + pctOf(c), 0) / collections.length,
        )
      : 0;

  const types = [...new Set(collections.map((c) => c.type))];

  const typeStat = (type: string) => {
    const set = collections.filter((c) => c.type === type);
    const avgPct =
      set.length > 0
        ? Math.round(
            set.reduce((s, c) => s + pctOf(c), 0) / set.length,
          )
        : 0;
    return { count: set.length, pct: avgPct };
  };

  const filters: [string, string, number][] = [
    ["all", "All", collections.length],
    ...types.map(
      (t) =>
        [t, t, collections.filter((c) => c.type === t).length] as [
          string,
          string,
          number,
        ],
    ),
  ];

  const shown =
    filterType === "all"
      ? collections
      : collections.filter((c) => c.type === filterType);

  const closest = [...inProgress].sort((a, b) => pctOf(b) - pctOf(a)).slice(0, 4);

  return (
    <div
      className="r-page"
      style={{
        padding: 28,
        overflow: "auto",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 20,
      }}
    >
      {/* hero row */}
      <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "1.35fr 1fr", gap: 20, flexShrink: 0 }}>
        {/* left: completionist stats */}
        <div
          className="atlas-topo"
          style={{
            position: "relative",
            borderRadius: "var(--radius-xl)",
            border: "1px solid var(--border-gold)",
            padding: 28,
            overflow: "hidden",
            boxShadow: "var(--glow-gold-sm), var(--ring-top)",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 26,
          }}
        >
          {isLoading ? (
            <div style={{ width: 124, height: 124, flexShrink: 0 }}>
              <Skel width="124px" height="124px" />
            </div>
          ) : (
            <ProgressRing value={avg} size={124} stroke={12} label="Avg complete" />
          )}

          <div className="r-basis-full" style={{ flex: 1, minWidth: 220 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>
              Completionist · Lifetime
            </div>

            {isLoading ? (
              <Skel height="46px" width="160px" />
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 10,
                  marginBottom: 8,
                }}
              >
                <span
                  className="atlas-gold-text"
                  style={{
                    fontFamily: "var(--font-display)",
                    fontWeight: 700,
                    fontSize: 46,
                    lineHeight: 1,
                    letterSpacing: "-.02em",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {completed.length}
                </span>
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 12,
                    color: "var(--text-muted)",
                    whiteSpace: "nowrap",
                  }}
                >
                  / {collections.length} collections completed
                </span>
              </div>
            )}

            <div
              style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}
            >
              <Badge variant="gold" dot>
                {collections.length} collections tracked
              </Badge>
              <Badge variant="neutral">{inProgress.length} in progress</Badge>
            </div>

            {!isLoading && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "14px 26px" }}>
                {types.map((t) => {
                  const s = typeStat(t);
                  return (
                    <div
                      key={t}
                      style={{ display: "flex", alignItems: "center", gap: 11 }}
                    >
                      <span
                        style={{
                          width: 40,
                          height: 40,
                          flex: "0 0 auto",
                          borderRadius: "var(--radius-md)",
                          display: "grid",
                          placeItems: "center",
                          background: "var(--accent-soft)",
                          border: "1px solid var(--border-gold)",
                          color: "var(--gold-400)",
                        }}
                      >
                        {TYPE_ICON[t] ?? <LayoutGrid size={19} />}
                      </span>
                      <div>
                        <div
                          style={{
                            fontFamily: "var(--font-display)",
                            fontWeight: 700,
                            fontSize: 18,
                            color: "var(--text-primary)",
                            lineHeight: 1,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {s.pct}
                          <span
                            style={{
                              fontSize: 12,
                              color: "var(--text-faint)",
                              fontWeight: 500,
                            }}
                          >
                            %
                          </span>
                        </div>
                        <div className="eyebrow" style={{ fontSize: 9.5, marginTop: 4 }}>
                          {t} · {s.count}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* right: closest to completion */}
        <AtlasPanel
          title="Closest to completion"
          action={<Badge variant="gold">{inProgress.length} active</Badge>}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 15 }}>
            {isLoading ? (
              [0, 1, 2, 3].map((i) => <Skel key={i} height="44px" />)
            ) : !isAuthenticated ? (
              <EmptyState
                icon={<LogIn size={18} />}
                title="Sign up to track your progress"
                hint="Create a free account to see which collections you're closest to completing."
                action={{ label: "Sign up", onClick: () => void navigate({ to: "/signup" }) }}
              />
            ) : (
              closest.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => void navigate(collectionTarget(c))}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 13,
                      width: "100%",
                      textAlign: "left",
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      font: "inherit",
                    }}
                  >
                    <span
                      style={{
                        width: 38,
                        height: 38,
                        flex: "0 0 auto",
                        borderRadius: "var(--radius-md)",
                        overflow: "hidden",
                        display: "grid",
                        placeItems: "center",
                        background: "var(--surface-sunken)",
                        border: "1px solid var(--border-default)",
                        color: "var(--gold-400)",
                      }}
                    >
                      {c.coverImage ? (
                        <img
                          src={c.coverImage}
                          alt=""
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        (TYPE_ICON[c.type] ?? <LayoutGrid size={19} />)
                      )}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "baseline",
                          gap: 8,
                          marginBottom: 6,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13.5,
                            fontWeight: 600,
                            color: "var(--text-primary)",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {c.name}
                        </span>
                        <span
                          style={{
                            fontFamily: "var(--font-mono)",
                            fontSize: 11,
                            color: "var(--text-muted)",
                            flex: "0 0 auto",
                          }}
                        >
                          {pctOf(c)}%
                        </span>
                      </div>
                      <ProgressBar
                        value={pctOf(c)}
                        max={100}
                        showValue={false}
                        size="sm"
                      />
                    </div>
                  </button>
                ))
            )}
          </div>
        </AtlasPanel>
      </div>

      {/* filter bar */}
      <div
        className="r-wrap"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <div
          className="r-scroll-x"
          style={{
            display: "flex",
            gap: 6,
            padding: 4,
            background: "var(--surface-card)",
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-pill)",
          }}
        >
          {filters.map(([id, label, count]) => {
            const active = filterType === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setFilterType(id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  padding: "0 16px",
                  height: 34,
                  borderRadius: "var(--radius-pill)",
                  cursor: "pointer",
                  border: "none",
                  fontFamily: "var(--font-sans)",
                  fontSize: 13,
                  fontWeight: 600,
                  background: active ? "var(--accent)" : "transparent",
                  color: active ? "var(--text-on-gold)" : "var(--text-secondary)",
                  boxShadow: active ? "var(--glow-gold-sm)" : "none",
                  transition: "var(--t-colors)",
                }}
              >
                {label}
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    fontWeight: 700,
                    padding: "1px 6px",
                    borderRadius: "var(--radius-pill)",
                    background: active ? "rgba(0,0,0,.16)" : "var(--surface-sunken)",
                    color: active ? "var(--text-on-gold)" : "var(--text-muted)",
                  }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        <div style={{ flex: "1 1 280px", maxWidth: 420, minWidth: 0 }}>
          <SiteSearch size="md" placeholder="Search peaks, lists, trails, landmarks" />
        </div>
      </div>

      {/* 2-column grid */}
      <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {isLoading
          ? [0, 1, 2, 3].map((i) => <Skel key={i} height="88px" />)
          : shown.map((c) => (
              <CollectionCard
                key={c.id}
                title={c.name}
                type={c.type}
                value={c.completedCount}
                max={c.itemCount}
                pct={pctOf(c)}
                distance={trailDistanceOf(c)}
                note={companionNote(c.slug, allCollections)}
                image={c.coverImage}
                icon={TYPE_ICON_LG[c.type] ?? <LayoutGrid size={26} />}
                onClick={() => void navigate(collectionTarget(c))}
              />
            ))}
      </div>
    </div>
  );
}
